import { FC } from 'react';
import { useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { CustomTable } from 'src/presentations/components/molecules/table/Table';
import { formatDate } from 'src/utils/date';
import { beautifyEnum } from 'src/utils/utility';
import { CustomTooltip } from 'src/presentations/components/atom/customtooltip';
import IconInfo from 'src/assets/images/icons/information-outline-grey.svg';
import { SelectedShippingServiceItem } from './types';

interface INewServiceTableProps {
  data: SelectedShippingServiceItem;
}

const useColumns = (): MRT_ColumnDef<SelectedShippingServiceItem>[] => {
  return [
    {
      enableEditing: false,
      accessorKey: 'route',
      header: 'Trade Lane',
      accessorFn: (row) => {
        // Data comes from newMatchingService.existingShippingService which has the merged selected service data
        const listZone = row?.existingShippingService?.listZone;
        const route = row?.existingShippingService?.route;
        const modality = row?.existingShippingService?.modality;

        return (
          <div className='flex items-center gap-10 min-w-[140px]'>
            <div className='relative'>{route || '-'}</div>
            {modality === 'COURIER' && listZone && listZone.length > 0 && (
              <CustomTooltip
                title={
                  <div className='grid grid-cols-2 gap-8'>
                    {listZone
                      ?.slice()
                      ?.sort((a, b) => {
                        if (a.pol?.localeCompare(b.pol) !== 0) {
                          return a.pol?.localeCompare(b.pol);
                        }
                        return a.pod?.localeCompare(b.pod);
                      })
                      ?.map((item) => (
                        <div key={`${item?.pol}_${item?.pod}`} className='py-8 border-b border-neutral-70'>
                          {item?.pol} / {item?.pod}
                        </div>
                      ))}
                  </div>
                }
              >
                <img width={20} src={IconInfo} alt='' />
              </CustomTooltip>
            )}
          </div>
        );
      },
    },
    {
      enableEditing: false,
      accessorKey: 'shipperName',
      header: 'Shipper Code',
      accessorFn: (row) => <span>{row?.existingShippingService?.shipperName ? beautifyEnum(row.existingShippingService.shipperName).toUpperCase() : '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'consigneeName',
      header: 'Consignee Code',
      accessorFn: (row) => (
        <span>{row?.existingShippingService?.consigneeName ? beautifyEnum(row.existingShippingService.consigneeName).toUpperCase() : '-'}</span>
      ),
    },
    {
      header: 'Billed Entity',
      enableEditing: false,
      accessorKey: 'billingPartyCode',
      accessorFn: (row) => (
        <span>{row?.existingShippingService?.billingPartyCode ? beautifyEnum(row.existingShippingService.billingPartyCode).toUpperCase() : '-'}</span>
      ),
    },
    {
      enableEditing: false,
      accessorKey: 'service',
      header: 'Service',
      accessorFn: (row) => <span>{row?.existingShippingService?.service || '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'type',
      header: 'Type',
      accessorFn: (row) => <span>{row?.existingShippingService?.type || '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'modality',
      header: 'Modality',
      accessorFn: (row) => <span>{row?.existingShippingService?.modality || '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'carrier',
      header: 'Carrier',
      accessorFn: (row) => <span>{row?.existingShippingService?.carrier || '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'incoterm',
      header: 'Incoterm',
      accessorFn: (row) => <span>{row?.existingShippingService?.incoterm || '-'}</span>,
    },
    {
      header: 'Validity Period',
      id: 'validityPeriod',
      accessorFn: (row) => {
        // Display validity period from form fields
        const startDate = row?.existingShippingService?.validityPeriod?.start;
        const endDate = row?.existingShippingService?.validityPeriod?.end;

        if (startDate && endDate) {
          return (
            <span>
              {formatDate(new Date(startDate))} - {formatDate(new Date(endDate))}
            </span>
          );
        }
        return '-';
      },
      enableEditing: false,
    },
  ];
};

export const TableNewService: FC<INewServiceTableProps> = (props) => {
  const { data } = props;

  const columns = useColumns();

  const table = useMaterialReactTable({
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing: false,
    editDisplayMode: 'table',
    enableRowSelection: false,
    enableMultiRowSelection: false,
    data: [data], // Wrap in array since table expects array
    columns,
    muiTableProps: {
      sx: {
        borderCollapse: 'separate',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
  });

  return <CustomTable table={table} />;
};
